import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { getBranchStock, BranchStockItem, adjustStock } from '../../services/stockService';
import { getBranches, Branch } from '../../services/branchService';
import { getCategories } from '../../services/itemService';
import { 
  ClipboardCheck, 
  Search, 
  Scan, 
  AlertTriangle, 
  CheckCircle, 
  Save, 
  RotateCcw,
  TrendingUp,
  TrendingDown,
  Download,
  Printer,
  X
} from 'lucide-react';
import BarcodeScanner from '../../components/scanner/BarcodeScanner';

interface OpnameItem extends BranchStockItem {
  actual_qty: number;
  difference: number;
  counted: boolean;
  notes: string;
}

const StockOpname: React.FC = () => {
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [selectedBranch, setSelectedBranch] = useState<number>(currentUser?.branch_id || 0);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  
  const [opnameItems, setOpnameItems] = useState<OpnameItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [scanMode, setScanMode] = useState(false);
  const [showDifferencesOnly, setShowDifferencesOnly] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  
  const barcodeInputRef = useRef<HTMLInputElement>(null);
  const scanBufferRef = useRef<string>('');
  const scanTimerRef = useRef<number | null>(null);

  useEffect(() => {
    loadBranches();
    loadCategories();
  }, []);

  useEffect(() => {
    if (selectedBranch > 0) {
      loadStockForOpname();
    }
  }, [selectedBranch, selectedCategory]);

  useEffect(() => {
    if (scanMode && barcodeInputRef.current) {
      barcodeInputRef.current.focus();
    }
  }, [scanMode]);

  const loadBranches = async () => {
    try {
      const data = await getBranches();
      setBranches(data);
      if (currentUser?.role !== 'owner' && currentUser?.branch_id) {
        setSelectedBranch(currentUser.branch_id);
      } else if (data.length > 0 && !selectedBranch) {
        setSelectedBranch(data[0].id);
      }
    } catch (err: any) {
      console.error('Failed to load branches:', err);
    }
  };

  const loadCategories = async () => {
    try {
      const cats = await getCategories();
      setCategories(cats);
    } catch (err: any) {
      console.error('Failed to load categories:', err);
    }
  };

  const loadStockForOpname = async () => {
    if (!selectedBranch) return;
    
    setLoading(true);
    try {
      const data = await getBranchStock(selectedBranch, {
        category: selectedCategory || undefined,
      });
      
      // Initialize opname items
      const opnameData: OpnameItem[] = data.map(item => ({
        ...item,
        actual_qty: 0,
        difference: 0,
        counted: false,
        notes: ''
      }));
      
      setOpnameItems(opnameData);
    } catch (err: any) {
      alert('Failed to load stock: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleScanInput = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const input = e.currentTarget;
    
    if (e.key === 'Enter' && input.value.trim()) {
      e.preventDefault();
      const scanned = input.value.trim();
      
      // Find item by SKU or barcode
      const item = opnameItems.find(i => 
        i.sku.toLowerCase() === scanned.toLowerCase() ||
        i.name.toLowerCase().includes(scanned.toLowerCase())
      );
      
      if (item) {
        // Focus on the actual qty input for this item
        const qtyInput = document.getElementById(`qty-${item.item_id}`) as HTMLInputElement;
        if (qtyInput) {
          qtyInput.focus();
          qtyInput.select();
        }
        setSearchQuery(scanned);
      } else {
        alert(`Item tidak ditemukan: ${scanned}`);
      }
      
      input.value = '';
    }
  };

  const handleActualQtyChange = (itemId: number, actualQty: number) => {
    setOpnameItems(prev => prev.map(item => {
      if (item.item_id === itemId) {
        const difference = actualQty - item.qty;
        return {
          ...item,
          actual_qty: actualQty,
          difference,
          counted: true
        };
      }
      return item;
    }));
  };

  const handleNotesChange = (itemId: number, notes: string) => {
    setOpnameItems(prev => prev.map(item => {
      if (item.item_id === itemId) {
        return { ...item, notes };
      }
      return item;
    }));
  };

  const handleQuickCount = (itemId: number) => {
    // Mark as counted with current system qty (no difference)
    setOpnameItems(prev => prev.map(item => {
      if (item.item_id === itemId) {
        return {
          ...item,
          actual_qty: item.qty,
          difference: 0,
          counted: true,
          notes: item.notes || 'Qty sesuai sistem'
        };
      }
      return item;
    }));
  };

  const handleResetItem = (itemId: number) => {
    setOpnameItems(prev => prev.map(item => {
      if (item.item_id === itemId) {
        return {
          ...item,
          actual_qty: 0,
          difference: 0,
          counted: false,
          notes: ''
        };
      }
      return item;
    }));
  };

  const handleSaveOpname = async () => {
    const itemsToAdjust = opnameItems.filter(item => item.counted && item.difference !== 0);
    
    if (itemsToAdjust.length === 0) {
      alert('Tidak ada perubahan untuk disimpan');
      return;
    }

    const confirmMsg = `Akan menyesuaikan ${itemsToAdjust.length} item.\n\n` +
      `Total kenaikan: ${itemsToAdjust.filter(i => i.difference > 0).reduce((sum, i) => sum + i.difference, 0)}\n` +
      `Total penurunan: ${Math.abs(itemsToAdjust.filter(i => i.difference < 0).reduce((sum, i) => sum + i.difference, 0))}\n\n` +
      `Lanjutkan?`;

    if (!confirm(confirmMsg)) return;

    setSaving(true);
    let successCount = 0;
    let failedCount = 0;

    try {
      for (const item of itemsToAdjust) {
        try {
          const notes = item.notes || `Stock opname: ${item.difference > 0 ? '+' : ''}${item.difference}`;
          await adjustStock(item.item_id, selectedBranch, item.actual_qty, notes);
          successCount++;
        } catch (err) {
          console.error(`Failed to adjust ${item.name}:`, err);
          failedCount++;
        }
      }

      alert(`Stock opname selesai!\n\nBerhasil: ${successCount}\nGagal: ${failedCount}`);
      
      if (successCount > 0) {
        // Reload stock data
        loadStockForOpname();
      }
    } catch (err: any) {
      alert('Error saat menyimpan: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleExportOpname = () => {
    const csv = [
      ['SKU', 'Nama', 'Kategori', 'Qty Sistem', 'Qty Aktual', 'Selisih', 'Status', 'Keterangan'].join(','),
      ...opnameItems.map(item => [
        item.sku,
        `"${item.name}"`,
        item.category,
        item.qty,
        item.counted ? item.actual_qty : '-',
        item.counted ? item.difference : '-',
        item.counted ? (item.difference === 0 ? 'OK' : 'DIFFERENT') : 'NOT COUNTED',
        `"${item.notes}"`
      ].join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `opname_${selectedBranch}_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  // Filter and search
  const filteredItems = opnameItems.filter(item => {
    const matchSearch = item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                       item.sku.toLowerCase().includes(searchQuery.toLowerCase());
    const matchDifference = !showDifferencesOnly || (item.counted && item.difference !== 0);
    return matchSearch && matchDifference;
  });

  // Statistics
  const totalItems = opnameItems.length;
  const countedItems = opnameItems.filter(i => i.counted).length;
  const itemsWithDifference = opnameItems.filter(i => i.counted && i.difference !== 0).length;
  const totalIncrease = opnameItems.filter(i => i.difference > 0).reduce((sum, i) => sum + i.difference, 0);
  const totalDecrease = Math.abs(opnameItems.filter(i => i.difference < 0).reduce((sum, i) => sum + i.difference, 0));
  const progress = totalItems > 0 ? (countedItems / totalItems) * 100 : 0;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center">
              <ClipboardCheck className="w-8 h-8 mr-3 text-blue-600" />
              Stock Opname
            </h1>
            <p className="text-gray-600">Physical inventory counting & adjustment</p>
          </div>

          <div className="flex items-center space-x-3">
            <button
              onClick={handleExportOpname}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center"
            >
              <Download className="w-5 h-5 mr-2" />
              Export CSV
            </button>
            <button
              onClick={handleSaveOpname}
              disabled={saving || itemsWithDifference === 0}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-5 h-5 mr-2" />
                  Save Opname ({itemsWithDifference})
                </>
              )}
            </button>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="bg-white rounded-lg shadow p-4 mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Progress</span>
            <span className="text-sm font-semibold text-blue-600">
              {countedItems} / {totalItems} ({progress.toFixed(1)}%)
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div
              className="bg-blue-600 h-3 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600 mb-1">Total Items</div>
            <div className="text-2xl font-bold text-gray-900">{totalItems}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600 mb-1">Counted</div>
            <div className="text-2xl font-bold text-blue-600">{countedItems}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600 mb-1">Total Increase</div>
            <div className="text-2xl font-bold text-green-600">+{totalIncrease}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600 mb-1">Total Decrease</div>
            <div className="text-2xl font-bold text-red-600">-{totalDecrease}</div>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {/* Branch */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Cabang
            </label>
            <select
              value={selectedBranch}
              onChange={(e) => setSelectedBranch(Number(e.target.value))}
              disabled={currentUser?.role !== 'owner' || loading}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {branches.map(branch => (
                <option key={branch.id} value={branch.id}>
                  {branch.code} - {branch.name}
                </option>
              ))}
            </select>
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Kategori
            </label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              disabled={loading}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Semua Kategori</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Cari Item
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Nama atau SKU..."
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Scan Mode */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Barcode Scanner
            </label>
            <div className="flex gap-2">
              <button
                onClick={() => setScanMode(!scanMode)}
                className={`flex-1 px-3 py-2 rounded-lg border-2 transition-colors flex items-center justify-center ${
                  scanMode 
                    ? 'bg-blue-600 text-white border-blue-600' 
                    : 'bg-white text-gray-700 border-gray-300 hover:border-blue-500'
                }`}
              >
                <Scan className="w-5 h-5 mr-2" />
                {scanMode ? 'Scanning...' : 'Enable Scanner'}
              </button>
              <button
                onClick={() => setShowScanner(true)}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                title="Open Camera Scanner"
              >
                📷
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className="flex items-end">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showDifferencesOnly}
                onChange={(e) => setShowDifferencesOnly(e.target.checked)}
                className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-gray-700">
                Selisih Saja
              </span>
            </label>
          </div>
        </div>

        {/* Scan Input (Hidden but active) */}
        {scanMode && (
          <div className="mt-4 p-3 bg-blue-50 border-2 border-blue-300 rounded-lg">
            <div className="flex items-center mb-2">
              <Scan className="w-5 h-5 text-blue-600 mr-2 animate-pulse" />
              <span className="text-sm font-medium text-blue-900">Scanner Mode Active - Scan barcode or type SKU</span>
            </div>
            <input
              ref={barcodeInputRef}
              type="text"
              onKeyDown={handleScanInput}
              placeholder="Scan atau ketik SKU..."
              className="w-full px-4 py-2 border-2 border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
              autoFocus
            />
          </div>
        )}
      </div>

      {/* Items Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <p className="mt-4 text-gray-600">Loading items...</p>
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="text-center py-12">
            <ClipboardCheck className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600">No items found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nama</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kategori</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Qty Sistem</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Qty Aktual</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Selisih</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Keterangan</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredItems.map((item) => (
                  <tr 
                    key={item.item_id} 
                    className={`hover:bg-gray-50 ${
                      item.counted 
                        ? item.difference === 0 
                          ? 'bg-green-50' 
                          : 'bg-yellow-50'
                        : ''
                    }`}
                  >
                    <td className="px-4 py-3 whitespace-nowrap">
                      {item.counted ? (
                        item.difference === 0 ? (
                          <CheckCircle className="w-6 h-6 text-green-600" />
                        ) : (
                          <AlertTriangle className="w-6 h-6 text-orange-600" />
                        )
                      ) : (
                        <div className="w-6 h-6 border-2 border-gray-300 rounded" />
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                      {item.sku}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {item.name}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                      {item.category}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-center text-sm font-semibold text-gray-900">
                      {item.qty}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-center">
                      <input
                        id={`qty-${item.item_id}`}
                        type="number"
                        value={item.actual_qty}
                        onChange={(e) => handleActualQtyChange(item.item_id, parseInt(e.target.value) || 0)}
                        min="0"
                        className="w-24 px-2 py-1 text-center border-2 border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-center">
                      {item.counted && (
                        <div className="flex items-center justify-center">
                          {item.difference > 0 ? (
                            <TrendingUp className="w-5 h-5 text-green-600 mr-1" />
                          ) : item.difference < 0 ? (
                            <TrendingDown className="w-5 h-5 text-red-600 mr-1" />
                          ) : null}
                          <span className={`font-bold ${
                            item.difference > 0 ? 'text-green-600' :
                            item.difference < 0 ? 'text-red-600' :
                            'text-gray-600'
                          }`}>
                            {item.difference > 0 ? '+' : ''}{item.difference}
                          </span>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <input
                        type="text"
                        value={item.notes}
                        onChange={(e) => handleNotesChange(item.item_id, e.target.value)}
                        placeholder="Keterangan..."
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-center">
                      <button
                        onClick={() => handleQuickCount(item.item_id)}
                        className="text-blue-600 hover:text-blue-900 mr-2 text-xs"
                        title="Mark as OK (same qty)"
                      >
                        OK
                      </button>
                      {item.counted && (
                        <button
                          onClick={() => handleResetItem(item.item_id)}
                          className="text-red-600 hover:text-red-900 text-xs"
                          title="Reset"
                        >
                          <RotateCcw className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Summary Panel */}
      {itemsWithDifference > 0 && (
        <div className="mt-6 bg-gradient-to-r from-orange-50 to-yellow-50 border-l-4 border-orange-500 rounded-lg shadow p-6">
          <h3 className="text-lg font-bold text-orange-900 mb-4 flex items-center">
            <AlertTriangle className="w-6 h-6 mr-2" />
            Ringkasan Perubahan
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg p-4 border border-orange-200">
              <div className="text-sm text-gray-600">Items dengan Selisih</div>
              <div className="text-2xl font-bold text-orange-600">{itemsWithDifference}</div>
            </div>
            <div className="bg-white rounded-lg p-4 border border-green-200">
              <div className="text-sm text-gray-600">Total Penambahan</div>
              <div className="text-2xl font-bold text-green-600">+{totalIncrease}</div>
            </div>
            <div className="bg-white rounded-lg p-4 border border-red-200">
              <div className="text-sm text-gray-600">Total Pengurangan</div>
              <div className="text-2xl font-bold text-red-600">-{totalDecrease}</div>
            </div>
          </div>
          <div className="mt-4 text-sm text-orange-800">
            Klik tombol "Save Opname" untuk menyimpan semua perubahan ke sistem.
          </div>
        </div>
      )}
      
      {/* Barcode Scanner Modal */}
      <BarcodeScanner
        isOpen={showScanner}
        onScan={(code) => {
          // Find item by SKU or barcode
          const item = opnameItems.find(i => 
            i.sku.toLowerCase() === code.toLowerCase() ||
            i.barcode?.toLowerCase() === code.toLowerCase()
          );
          
          if (item) {
            // Update actual qty
            setOpnameItems(prev => prev.map(i => 
              i.item_id === item.item_id
                ? {
                    ...i,
                    actual_qty: i.actual_qty + 1,
                    difference: (i.actual_qty + 1) - i.quantity,
                    counted: true
                  }
                : i
            ));
          } else {
            alert(`Item dengan kode ${code} tidak ditemukan`);
          }
          setShowScanner(false);
        }}
        onClose={() => setShowScanner(false)}
      />
    </div>
  );
};

export default StockOpname;
